
<script>
import {
  required,
  email,
  numeric,
  minValue,
  maxValue
} from "@vuelidate/validators";
import { tel, zipCode } from "@/utils/form-validators";
import dateUtils from "@/utils/date";
import FormFieldset from "@/components/molecules/form-fieldset";
import ButtonGroup from "@/components/molecules/button-group";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormRadio from "@/components/molecules/form-radio";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import ButtonIcon from "@/components/molecules/button-icon";
import FormError from "@/components/molecules/form/form-error";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import { useForms } from "@/components/organisms/forms/mixin";
import ArticleRetractationForm from "@/components/organisms/forms/retractation/article-retractation";

export default {
  name: "RetractationForm",
  components: {
    Form,
    FormFieldset,
    ButtonGroup,
    FormSelectOptGroup,
    FormSelect,
    FormGroup,
    FormSelectOption,
    FormInput,
    Button,
    ButtonIcon,
    FormRadio,
    FormError,
    Captcha,
    SuccessPopin,
    ArticleRetractationForm
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false,
      default: 121
    },
    error: {
      type: [Object, Error],
      required: false,
      default: null
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);
    const { redirectToPreviousPage } = useForms();

    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      id_magasin: this.magasinId ? this.magasinId : "",
      date: dateUtils,
      articles: [{ qte: "", designation: "", reference: "" }],
      date_retrait: "",
      num_commande: "",
      civilite: "",
      nom: "",
      prenom: "",
      email: "",
      tel: "",
      adresse: "",
      code_postal: "",
      ville: "",
      captchaToken: null,
      magasinChoisi: null
    };
  },
  computed: {
    otherMagasins() {
      if (!this.magasinId) {
        return this.magasins;
      }
      return this.magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  validations() {
    return {
      id_magasin: { required },
      date_retrait: { required },
      num_commande: { required },
      nom: { required },
      prenom: { required },
      civilite: { required },
      adresse: { required },
      tel: {
        required,
        tel
      },
      code_postal: {
        required,
        zipCode
      },
      ville: { required },
      email: {
        required,
        email
      }
    };
  },
  methods: {
    selectMagasin() {
      this.magasins.find((magasin) => {
        if (magasin.id === this.id_magasin) {
          this.magasinChoisi = magasin;
        }
      });
    },
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        id_magasin: this.id_magasin,
        articles: this.articles,
        date_retrait: this.date_retrait,
        num_commande: this.num_commande,
        nom: this.nom,
        prenom: this.prenom,
        civilite: this.civilite,
        adresse: this.adresse,
        tel: this.tel,
        code_postal: this.code_postal,
        ville: this.ville,
        email: this.email,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    },
    addItem() {
      this.articles.push({ qte: "", designation: "", reference: "" });
    },
    removeItem(i) {
      const articlesFiltered = this.articles.filter(
        (article, index) => index !== i
      );
      this.articles = articlesFiltered;
    },
    handleKeypress(event) {
      if (
        event.which !== 8 &&
        event.which !== 0 &&
        (event.which < 48 || event.which > 57)
      ) {
        event.preventDefault();
      }
    }
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <FormError
      v-if="error"
      :error="error"
    />
    <br>
    <div>
      <p class="c-inscription-form__explanation">
        Veuillez compléter et renvoyer le présent formulaire uniquement si vous souhaitez vous rétracter du contrat.
      </p>
    </div>
    <div style="padding: 30px">
      <strong v-if="magasinChoisi ||currentMagasin">A l'attention de : </strong>
      <div v-if="magasinChoisi">
        {{ magasinChoisi.attributes.magasin }} <br /> {{ magasinChoisi.attributes.adresse }} <br /> {{ magasinChoisi.attributes.cp }} {{ magasinChoisi.attributes.ville }} <br /> {{ magasinChoisi.attributes.telephone_magasin }}<br />{{ magasinChoisi.attributes.email_magasin }}
      </div>
      <div v-else-if="currentMagasin">
        {{ currentMagasin.attributes.magasin }} <br /> {{ currentMagasin.attributes.adresse }} <br /> {{ currentMagasin.attributes.cp }} {{ currentMagasin.attributes.ville }}<br />{{ currentMagasin.attributes.telephone_magasin }}<br />{{ currentMagasin.attributes.email_magasin }}
      </div>
    </div>
    <Form
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset
        center
        size="full"
      >
        <FormGroup
          :state="getState(v$,'id_magasin')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label for="magasinReference">
            <h2 class="u-font-19 u-mb-xs">
              Votre magasin de référence*
            </h2>
          </label>
          <FormSelect
            id="magasinReference"
            v-model="id_magasin"
            :state="getState(v$,'id_magasin')"
            :default-value="'Sélectionnez un magasin'"
            size="full"
            @input="selectMagasin"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins Cora">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <p>
              <template v-if="v$.id_magasin.required.$invalid">
                Merci de choisir un magasin
              </template>
              <template v-else>
                {{ getErrorMessage("id_magasin") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <p class="c-inscription-form__explanation">
          Je vous notifie par la présente ma rétractation du contrat portant sur la vente du bien / pour la prestation de service ci-dessous :
        </p>
        <br>
        <p class="c-inscription-form__explanation">
          Indiquez le ou les produits qui font l’objet de votre rétractation :
        </p>
        <ArticleRetractationForm
          v-for="(article,i) in articles"
          :key="i"
          v-model="articles[i]"
          class="u-mb-md"
          @removeItem="removeItem(i)"
        />
        <br>
        <ButtonGroup center>
          <ButtonIcon
            variant="primary"
            radius="full"
            icon-name="plus"
            icon-color="white"
            size="md"
            :disabled="articles.length === 10"
            @click="addItem"
          >
            Ajouter un article
          </ButtonIcon>
        </ButtonGroup>
        <br>
        <p class="c-inscription-form__explanation">
          Les informations de votre commande :
        </p>

        <FormGroup
          :state="getState(v$,'date_retrait')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Commandé le / reçu le *
          </label>

          <FormInput
            v-model.trim="date_retrait"
            :state="getState(v$,'date_retrait')"
            type="date"
            size="full"
            @blur="v$.date_retrait.$touch()"
            @input="v$.date_retrait.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.date_retrait.required.$invalid">
                Merci de renseigner votre date de commande
              </template>
              <template v-else>
                {{ getErrorMessage("date_retrait") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'num_commande')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Numéro de commande *
          </label>
          <FormInput
            id="numCommande"
            v-model.trim="num_commande"
            :state="getState(v$,'num_commande')"
            size="full"
            @blur="v$.num_commande.$touch()"
            @input="v$.num_commande.$reset()"
          >
            exemple : DRV-MB-564564
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.num_commande.required.$invalid">
                Merci de renseigner votre numéro de commande
              </template>
              <template v-else>
                {{ getErrorMessage("num_commande") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'coordonnees')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <h2 class="u-font-19 u-mb-xs">
            Vos coordonnées
          </h2>
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
          <FormGroup
            :state="getState(v$,'civilite')"
            inline
            class="c-inscription-form__form-group"
          >
            <FormRadio
              v-model="civilite"
              name="civilite"
              value="Mme"
              class="u-mr-lg"
            >
              Madame
            </FormRadio>
            <FormRadio
              v-model="civilite"
              name="civilite"
              value="M."
            >
              Monsieur
            </FormRadio>
            <template #message>
              <p>
                <template v-if="v$.civilite.required.$invalid">
                  Merci de renseigner votre civilité
                </template>
                <template v-else>
                  {{ getErrorMessage("civilite") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'nom')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Nom *
            </label>
            <FormInput
              id="nom"
              v-model.trim="nom"
              :state="getState(v$,'nom')"
              maxlength="60"
              size="full"
              @blur="v$.nom.$touch()"
              @input="v$.nom.$reset()"
            >
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.nom.required.$invalid">
                  Merci de renseigner votre nom
                </template>
                <template v-else>
                  {{ getErrorMessage("nom") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'prenom')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Prénom *
            </label>
            <FormInput
              id="prenom"
              v-model.trim="prenom"
              :state="getState(v$,'prenom')"
              maxlength="40"
              size="full"
              @blur="v$.prenom.$touch()"
              @input="v$.prenom.$reset()"
            >
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.prenom.required.$invalid">
                  Merci de renseigner votre prénom
                </template>
                <template v-else>
                  {{ getErrorMessage("prenom") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'email')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Email *
            </label>
            <FormInput
              id="email"
              v-model.trim="email"
              :state="getState(v$,'email')"
              size="full"
              @blur="v$.email.$touch()"
              @input="v$.email.$reset()"
            >
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.email.required.$invalid">
                  Merci de confirmer votre adresse e-mail
                </template>
                <template v-else-if="v$.email.email.$invalid">
                  Le format de cette adresse e-mail est invalide
                </template>
                <template v-else>
                  {{ getErrorMessage("email") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'tel')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Téléphone *
            </label>
            <FormInput
              id="tel"
              v-model.trim="tel"
              :state="getState(v$,'tel')"
              size="full"
              @blur="v$.tel.$touch()"
              @input="v$.tel.$reset()"
            >
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.tel.required.$invalid">
                  Merci de renseigner votre numéro de téléphone
                </template>
                <template v-else-if="v$.tel.tel.$invalid">
                  Merci de saisir un numéro valide
                </template>
                <template v-else>
                  {{ getErrorMessage("tel") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'adresse')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Adresse postale *
            </label>
            <FormInput
              id="adresse"
              v-model.trim="adresse"
              :state="getState(v$,'adresse')"
              maxlength="255"
              size="full"
              @blur="v$.adresse.$touch()"
              @input="v$.adresse.$reset()"
            >
              exemple : 16, place des belettes
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.adresse.required.$invalid">
                  Merci de renseigner votre adresse postale
                </template>
                <template v-else>
                  {{ getErrorMessage("adresse") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'ville')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Ville *
            </label>
            <FormInput
              id="ville"
              v-model.trim="ville"
              :state="getState(v$,'ville')"
              size="full"
              @blur="v$.ville.$touch()"
              @input="v$.ville.$reset()"
            >
              votre ville, exemple : Bondoufle
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.ville.required.$invalid">
                  Merci de renseigner votre ville
                </template>
                <template v-else>
                  {{ getErrorMessage("ville") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'code_postal')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Code postal *
            </label>
            <FormInput
              id="codePostale"
              v-model.trim="code_postal"
              :state="getState(v$,'code_postal')"
              size="full"
              @blur="v$.code_postal.$touch()"
              @input="v$.code_postal.$reset()"
            >
              votre code postal, exemple : 75000
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.code_postal.required.$invalid">
                  Merci de renseigner votre code postal
                </template>
                <template v-else>
                  {{ getErrorMessage("cp") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Date *
            </label>
            <FormInput
              size="full"
              disabled
            >
              {{ date.getTodayDate() }}
            </FormInput>
          </FormGroup>
        </FormGroup>
        <Captcha
          ref="captcha"
          @verify="verifyCaptcha"
        />
        <ButtonGroup center>
          <Button
            variant="secondary"
            radius="full"
            type="submit"
            value="Je valide ma demande"
            size="md"
            class="u-mt-sm u-flex-justify-center@sm"
            :disabled="state===states.LOADING"
          />
        </ButtonGroup>
      </FormFieldset>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
